import * as React from 'react';
import styled from 'styled-components';
import { ResponsiveValue } from 'styled-system';
import { media } from '@packages/ui';

import { ArrowLeft } from './ArrowLeft/ArrowLeft';
import { ArrowRight } from './ArrowRight/ArrowRight';
import { ChildrenContainer, ShadowBoxBigRightShadow, OverflowHideBase } from './SimpleSliderStyles';
import { ShownItems, breakpoints, initializeHolder, onArrowClickFactory } from './SimpleSlider';

export interface DYSliderProps {
  shownItems: ResponsiveValue<number>;
  className: string;
}

/**
 * Important - assumptions for inserted DY slides
 * 1) They should be wrapped into an element (div), that does not define its width, display or position
 * 2) Content of wrapping element should be done in a way that it stretches/shrinks with wrapper
 * 3) Width of wrapper is defined by slider. Height should be defined by wrapper/content, slider does not force it
 */
const ChildrenResizingContainer = styled(ChildrenContainer)<ShownItems>`
  > * {
    white-space: nowrap;
    box-sizing: border-box;
    width: 100%;
  }
  > * > style {
    display: none !important;
  }
  > * > * {
    display: inline-block;
    ${media.to.sm`
      width: ${(props: ShownItems) => 100 / props.xs}%;
      min-width: ${(props: ShownItems) => 100 / props.xs}%;
    `}

    ${breakpoints.map(
      (key) => media.from[key]`
        width: ${(props: ShownItems) => 100 / props[key]}%;
        min-width: ${(props: ShownItems) => 100 / props[key]}%;
    `,
    )}

    &:last-child {
      position: relative;
      &::after {
        position: absolute;
        content: '';
        ${media.to.md`
          right: -15px;
          width: 15px;
      `}
        right: -50px;
        width: 50px;
        height: 100%;
      }
    }
  }
`;
const MARGIN_BOTTOM = [3, 3, 5, 5, 6];

export const SimpleDYSlider: React.FC<DYSliderProps> = ({ shownItems, className, children }) => {
  const holderRef = React.useRef<HTMLDivElement | null>(null);
  const singleDivChildRef = React.useRef<HTMLDivElement | null>(null);
  const [arrowLeftDisabled, setArrowLeftDisabled] = React.useState<boolean>(true);
  const [arrowRightDisabled, setArrowRightDisabled] = React.useState<boolean>(false);
  const [reachedFinalPoint, setReachedFinalPoint] = React.useState<boolean>(false);
  const lastEvent = React.useRef<MouseEvent | TouchEvent | null>(null);
  const dyDivRef = React.useRef<HTMLDivElement | null>(null);
  // Slider should be invisible before non-empty content is inserted.
  // DY can insert empty content too (if campaign criteria not met for user etc.)
  const [isVisible, setIsVisible] = React.useState<boolean>(false);
  // Design requirements: IF not enogh items to display a slider,
  // hide controls and display items in center
  const [notEnoughItemsForSlider, setNotEnoughItemsForSlider] = React.useState<boolean>(false);
  const updateDYView = React.useCallback(() => {
    const dySliderPointer = singleDivChildRef.current?.children[0] as HTMLElement;
    if (dySliderPointer) {
      dySliderPointer.style.display = notEnoughItemsForSlider ? 'flex' : 'initial';
      dySliderPointer.style.justifyContent = notEnoughItemsForSlider ? 'center' : 'initial';
    }
  }, [notEnoughItemsForSlider]);

  const calculateIfEnoughItems = React.useCallback(() => {
    if (!holderRef.current || !singleDivChildRef.current) {
      return;
    }

    const children = Array.from(singleDivChildRef.current?.children[0]?.children || []) as HTMLElement[];

    let childrenLength = 0;
    children.forEach((child: HTMLElement) => {
      childrenLength += child.clientWidth || (child.getBoundingClientRect && child.getBoundingClientRect().width) || 0;
    });
    const isContentNotEnough = holderRef.current.clientWidth >= childrenLength;
    if (isContentNotEnough !== notEnoughItemsForSlider) {
      setNotEnoughItemsForSlider(isContentNotEnough);
      updateDYView();
    }
  }, [notEnoughItemsForSlider, updateDYView]);

  React.useEffect(() => {
    const onResize = () => calculateIfEnoughItems();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  });

  const [xs, sm, md, lg, xl, xxl] = React.useMemo(() => {
    const responsiveValues: number[] = ([] as number[]).concat(shownItems as number[]);
    return new Array(6).fill(responsiveValues[0]).map((_, index) => {
      return isNaN(responsiveValues[index]) ? responsiveValues[responsiveValues.length - 1] : responsiveValues[index];
    });
  }, [shownItems]);

  React.useEffect(() => {
    if (!singleDivChildRef.current) {
      return;
    }
    const onContentInsertion = () => {
      if (!singleDivChildRef.current?.children[0]) {
        return;
      }

      calculateIfEnoughItems();
      const dyMainDiv = singleDivChildRef.current?.children[0];
      // DY inserts <style> as first element
      const firstChild = dyMainDiv.children[1];
      if (firstChild) {
        if (!isVisible) {
          setIsVisible(true);
        }
        dyDivRef.current = dyMainDiv as HTMLDivElement;
        setTimeout(() => {
          updateDYView();
        }, 0);
      }
    };

    // Content is already inserted before hook trigger
    dyDivRef.current = (singleDivChildRef.current?.children[0] as HTMLDivElement) || null;
    if (dyDivRef.current) {
      onContentInsertion();
    }

    // Need to wait longer for content
    const mutationObserver = new MutationObserver(() => {
      onContentInsertion();
    });
    mutationObserver.observe(singleDivChildRef.current, {
      attributes: false,
      characterData: true,
      childList: true,
      subtree: true,
      attributeOldValue: false,
      characterDataOldValue: false,
    });
    return () => mutationObserver.disconnect();
  }, [calculateIfEnoughItems, isVisible, updateDYView]);

  React.useEffect(() => {
    return initializeHolder(
      holderRef,
      dyDivRef.current ? dyDivRef : singleDivChildRef,
      lastEvent,
      setArrowLeftDisabled,
      setArrowRightDisabled,
      false,
    );
  }, []);

  const scrollState = holderRef.current
    ? holderRef.current.scrollLeft / (holderRef.current.scrollWidth - holderRef.current.clientWidth) === 1
    : false;
  if (scrollState !== reachedFinalPoint) {
    setReachedFinalPoint(scrollState);
  }

  return (
    <OverflowHideBase mb={isVisible ? MARGIN_BOTTOM : 0}>
      <div ref={holderRef}>
        {!reachedFinalPoint && !notEnoughItemsForSlider && <ShadowBoxBigRightShadow />}
        <ChildrenResizingContainer ref={singleDivChildRef} className={className} {...{ xs, sm, md, lg, xl, xxl }}>
          {children}
        </ChildrenResizingContainer>
      </div>
      {notEnoughItemsForSlider || !isVisible ? (
        <></>
      ) : (
        <>
          <ArrowLeft
            onClick={onArrowClickFactory(false, holderRef, dyDivRef, setArrowLeftDisabled, setArrowRightDisabled)}
            disabled={arrowLeftDisabled}
          />
          <ArrowRight
            onClick={onArrowClickFactory(true, holderRef, dyDivRef, setArrowLeftDisabled, setArrowRightDisabled)}
            disabled={arrowRightDisabled}
          />
        </>
      )}
    </OverflowHideBase>
  );
};
